import { Box, Button, Flex, Image, Input, InputGroup, Text } from '@chakra-ui/react';
import { ChevronRight, Mail, Phone, ShieldCheck } from 'lucide-react';
import { colors } from 'styles/colors';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Controller } from 'react-hook-form';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatTime } from 'utils/formatTime';
import flag from 'assets/flag.webp';
import { usePhoneAuthStep, type Credential } from 'hooks/auth/usePhoneAuthStep';
import { useOtpAuthStep, OTP_LENGTH } from 'hooks/auth/useOtpAuthStep';

// ─── Phone step ──────────────────────────────────────────────────────────────

interface PhoneStepProps {
  onCredential: (c: Credential) => void;
}

const PhoneStep = ({ onCredential }: PhoneStepProps) => {
  const { formatMessage } = useIntl();
  const {
    dialingCode,
    form: { control },
    errors,
    isSubmitting,
    showCaptchaWidget,
    hcaptchaRef,
    hcaptchaSiteKey,
    handleSubmit,
    onCaptchaVerify,
    onCaptchaExpire,
    onCaptchaError,
  } = usePhoneAuthStep(onCredential);

  return (
    <Flex as='section' direction='column' w='100%' flex={1}>
      <Flex direction='column' align='center' mb={8}>
        <Flex
          align='center'
          justify='center'
          bg={colors.b2b.inviteIconBg}
          borderRadius='full'
          w={20}
          h={20}
          mb={5}
        >
          <Phone size={32} color={colors.b2b.accent} strokeWidth={1.5} />
        </Flex>
        <Text textStyle='headlineMedium' color='dark.D900' mb={2} textAlign='center'>
          <FormattedMessage id='invitation.authGate.title' defaultMessage='تأكيد رقم الهاتف' />
        </Text>
        <Text textStyle='bodySmallRegular' color='dark.D500' textAlign='center'>
          <FormattedMessage
            id='invitation.authGate.phoneHint'
            defaultMessage='أدخل رقم الهاتف اللي وصلتك عليه الدعوة'
          />
        </Text>
      </Flex>

      <form
        onSubmit={handleSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <Box flex={1}>
          <Text textStyle='bodySmallMedium' color='dark.D900' mb={1} textAlign='right'>
            <FormattedMessage id='loginModal.phoneNumber' defaultMessage='رقم الهاتف' />
          </Text>

          <InputGroup>
            <Controller
              defaultValue=''
              control={control}
              name='phone'
              render={({ field, fieldState: { error } }) => (
                <>
                  <Flex
                    align='center'
                    gap={1}
                    px={3}
                    h='40px'
                    borderWidth='1px'
                    borderColor={error ? 'red.500' : 'dark.D150'}
                    borderRightWidth={0}
                    borderLeftRadius={12}
                    bg='white'
                    pointerEvents='none'
                    flexShrink={0}
                  >
                    <Image src={flag} boxSize={5} alt='Iraq flag' />
                    <Text textStyle='bodySmallMedium' color='dark.D900' whiteSpace='nowrap'>
                      {dialingCode}
                    </Text>
                  </Flex>
                  <Input
                    placeholder='7XXXXXXXXX'
                    type='tel'
                    borderRightRadius={12}
                    borderLeftRadius={0}
                    borderLeftWidth={0}
                    color='dark.D900'
                    borderColor={error ? 'red.500' : 'dark.D150'}
                    focusBorderColor={error ? 'red.500' : colors.b2b.accent}
                    _focus={{ boxShadow: 'none' }}
                    {...field}
                  />
                </>
              )}
            />
          </InputGroup>

          {errors.phone && (
            <Text textStyle='bodySmallMedium' color='red.500' mt={2} textAlign='right'>
              {errors.phone.type === 'manual'
                ? errors.phone.message
                : formatMessage({
                    id: errors.phone.message!,
                    defaultMessage: 'Invalid phone number. Must start with 7 and be 10 digits.',
                  })}
            </Text>
          )}

          <Flex align='center' justify='end' gap={2} mt={4}>
            <Text textStyle='bodySmallRegular' color='dark.D500'>
              <FormattedMessage
                id='invitation.authGate.secureNote'
                defaultMessage='معلوماتك محمية ومشفرة'
              />
            </Text>
            <ShieldCheck size={16} color='#14532D' />
          </Flex>

          {showCaptchaWidget && (
            <Flex
              justifyContent='center'
              mt={4}
              width='100%'
              minH='78px'
              sx={{ '& iframe': { maxWidth: '100%' } }}
            >
              <HCaptcha
                ref={hcaptchaRef}
                sitekey={hcaptchaSiteKey}
                onVerify={onCaptchaVerify}
                onExpire={onCaptchaExpire}
                onError={onCaptchaError}
                sentry={false}
                reCaptchaCompat={false}
                size='normal'
                theme='light'
              />
            </Flex>
          )}
        </Box>

        <Button
          textAlign='center'
          minW='100%'
          type='submit'
          isLoading={isSubmitting}
          bg={colors.b2b.accent}
          color='white'
          _hover={{ bg: colors.b2b.accentHover }}
          _active={{ bg: colors.b2b.accentActive }}
          borderRadius='full'
          size='lg'
        >
          <FormattedMessage id='invitation.authGate.sendOtp' defaultMessage='إرسال الرمز' />
        </Button>
      </form>
    </Flex>
  );
};

// ─── OTP step ─────────────────────────────────────────────────────────────────

interface OtpStepProps {
  credential: Credential;
  onSessionRefresh: (session: string) => void;
  onBackToPhoneStep: () => void;
}

const OtpStep = ({ credential, onSessionRefresh, onBackToPhoneStep }: OtpStepProps) => {
  const {
    control,
    inputRefs,
    errors,
    isSubmitting,
    isResending,
    timer,
    handleSubmit,
    handleInputChange,
    handleKeyDown,
    onResend,
  } = useOtpAuthStep({ credential, onSessionRefresh, onBackToPhoneStep });

  return (
    <Flex as='section' direction='column' w='100%' flex={1}>
      <Flex direction='column' align='center' mb={8}>
        <Flex
          align='center'
          justify='center'
          bg={colors.b2b.inviteIconBg}
          borderRadius='full'
          w={20}
          h={20}
          mb={5}
        >
          <Mail size={32} color={colors.b2b.accent} strokeWidth={1.5} />
        </Flex>
        <Text textStyle='headlineMedium' color='dark.D900' mb={2} textAlign='center'>
          <FormattedMessage id='loginOtpModal.title' defaultMessage='أدخل رمز التحقق' />
        </Text>
        <Text textStyle='bodySmallRegular' color='dark.D500' textAlign='center' mb={1}>
          <FormattedMessage
            id='loginOtpModal.subtitle'
            defaultMessage='أرسلنا رمزاً مكوّناً من 6 أرقام إلى'
          />
        </Text>
        <Text textStyle='bodySmallMedium' color={colors.b2b.accent} textAlign='center'>
          {credential.phone}
        </Text>
      </Flex>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box flex={1}>
          <Flex justify='center' gap={2} mb={2}>
            {Array.from({ length: OTP_LENGTH }).map((_, index) => (
              <Controller
                key={index}
                defaultValue={0}
                name={`otp.${index}`}
                control={control}
                rules={{ required: true }}
                render={() => (
                  <Input
                    type='tel'
                    maxLength={1}
                    ref={(el) => (inputRefs.current[index] = el)}
                    onChange={(e) => handleInputChange(Number(e.target.value), index)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    borderRadius='12px'
                    textAlign='center'
                    color='dark.D900'
                    focusBorderColor={colors.b2b.accent}
                    borderColor={errors.otp ? colors.b2b.accent : 'dark.D150'}
                    width='44px'
                    height='52px'
                    padding={0}
                  />
                )}
              />
            ))}
          </Flex>

          {errors.otp && (
            <Text textStyle='bodySmallMedium' color='red' mt={2} textAlign='center'>
              {errors.otp.type === 'manual' && errors.otp.message ? (
                errors.otp.message
              ) : (
                <FormattedMessage
                  id='loginOtpModal.error'
                  defaultMessage='Please enter valid otp code'
                />
              )}
            </Text>
          )}

          <Flex justify='center' align='center' gap={1} mt={5}>
            <Text textStyle='bodySmallRegular' color='dark.D700'>
              <FormattedMessage id='loginOtpModal.resend' defaultMessage='لم يصلك الرمز؟' />
            </Text>
            {timer > 0 ? (
              <Text textStyle='bodySmallMedium' color={colors.b2b.accent}>
                {formatTime(timer)}
              </Text>
            ) : (
              <Button
                variant='link'
                color={colors.b2b.accent}
                fontWeight={500}
                fontSize='sm'
                p={0}
                h='auto'
                minH='auto'
                onClick={onResend}
                isLoading={isResending}
                isDisabled={isResending}
              >
                <FormattedMessage
                  id='loginOtpModal.resendButton'
                  defaultMessage='إعادة إرسال الرمز'
                />
              </Button>
            )}
          </Flex>
        </Box>

        <Button
          minW='100%'
          type='submit'
          isLoading={isSubmitting}
          bg={colors.b2b.accent}
          color='white'
          _hover={{ bg: colors.b2b.accentHover }}
          _active={{ bg: colors.b2b.accentActive }}
          borderRadius='full'
          size='lg'
        >
          <FormattedMessage id='invitation.authGate.verifyOtp' defaultMessage='تأكيد' />
        </Button>
      </form>
    </Flex>
  );
};

// ─── Auth gate container ──────────────────────────────────────────────────────

export const InvitationAuthGate = () => {
  const [credential, setCredential] = useState<Credential | null>(null);
  const [session, setSession] = useState('');

  if (credential) {
    return (
      <Flex direction='column' flex={1} bg='white'>
        <Flex
          justify='space-between'
          align='center'
          px={{ base: 3, sm: 4, md: 6 }}
          py={3}
          borderBottom='1px solid'
          borderColor='dark.D150'
        >
          <Text textStyle='bodySmallMedium' color='dark.D900'>
            <FormattedMessage id='invitation.authGate.title' defaultMessage='تأكيد رقم الهاتف' />
          </Text>
          <Button
            variant='ghost'
            onClick={() => setCredential(null)}
            px={2}
            minW='80px'
            color='dark.D700'
            rightIcon={<ChevronRight size={18} />}
            _hover={{ bg: 'dark.D100' }}
            borderRadius='8px'
          >
            <FormattedMessage id='global.back' defaultMessage='رجوع' />
          </Button>
        </Flex>
        <Flex direction='column' align='center' px={6} pt={8} pb={8} flex={1}>
          <Flex direction='column' w='100%' maxW='480px' flex={1}>
            <OtpStep
              credential={{ ...credential, session }}
              onSessionRefresh={(s) => setSession(s)}
              onBackToPhoneStep={() => setCredential(null)}
            />
          </Flex>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex direction='column' flex={1} align='center' px={6} pt={12} pb={8} bg='white'>
      <Flex direction='column' w='100%' maxW='480px' flex={1}>
        <PhoneStep
          onCredential={(c) => {
            setCredential(c);
            setSession(c.session);
          }}
        />
      </Flex>
    </Flex>
  );
};
